import React, { useMemo, useState } from "react";
import { DatePicker } from "@mui/x-date-pickers";
import { DateTime } from "luxon";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { formatFullDate } from "../core/date-formatting";

const stages = [
  { name: "Printer File Prep", durationInDays: 7, color: "#6C63FF" },
  { name: "Verify e-proof", durationInDays: 7, color: "#00A6A6" },
  { name: "Await Physical Proof", durationInDays: 21, color: "#F28E2B" },
  { name: "Verify Physical Proof", durationInDays: 7, color: "#E15759" },
  { name: "Time to Print", durationInDays: 60, color: "#59A14F" },
  { name: "Freight", durationInDays: 40, color: "#4E79A7" },
  { name: "Send to Amazon", durationInDays: 30, color: "#B07AA1" },
];

const virtualStage = {
  name: "Before art complete",
  color: "#D9DEE7",
};

/**
 * @param {import("luxon").DateTime} date
 */
const rollForwardToBusinessDay = (date) => {
  if (date.weekday === 6) {
    return date.plus({ days: 2 });
  }

  if (date.weekday === 7) {
    return date.plus({ days: 1 });
  }

  return date;
};

/**
 * @param {import("luxon").DateTime|null} targetDate
 */
const calculateTimeline = (targetDate) => {
  if (!targetDate?.isValid) {
    return [];
  }

  const normalizedTargetDate = rollForwardToBusinessDay(targetDate.startOf("day"));
  let stageEndDate = normalizedTargetDate;
  const timeline = [];

  for (let i = stages.length - 1; i >= 0; i--) {
    const stage = stages[i];
    const nominalStartDate = stageEndDate.minus({ days: stage.durationInDays });
    const startDate = rollForwardToBusinessDay(nominalStartDate);
    timeline.unshift({
      ...stage,
      startDate,
      endDate: stageEndDate,
      nominalStartDate,
      nominalEndDate: stageEndDate,
    });
    stageEndDate = startDate;
  }

  return timeline;
};

/**
 * @param {import("luxon").DateTime} date
 */
const formatDate = (date) => formatFullDate(date.toMillis());

/**
 * @param {import("luxon").DateTime} startDate
 * @param {import("luxon").DateTime} endDate
 */
const daysBetween = (startDate, endDate) => Math.max(0, endDate.diff(startDate, "days").days);

/**
 * @param {object} props
 * @param {ReturnType<typeof calculateTimeline>} props.timeline
 */
const TimelineBar = ({ timeline }) => {
  if (timeline.length === 0) {
    return null;
  }

  const today = DateTime.now().startOf("day");
  const finalStage = timeline[timeline.length - 1];
  const chartStartDate = today;
  const chartEndDate = finalStage.endDate > today ? finalStage.endDate : today.plus({ days: 1 });
  const totalDays = Math.max(1, daysBetween(chartStartDate, chartEndDate));
  const chartSegments = [
    {
      ...virtualStage,
      startDate: chartStartDate,
      endDate: timeline[0].startDate > chartStartDate ? timeline[0].startDate : chartStartDate,
    },
    ...timeline,
  ]
    .map((stage) => {
      const startDate = stage.startDate < chartStartDate ? chartStartDate : stage.startDate;
      const endDate = stage.endDate > chartEndDate ? chartEndDate : stage.endDate;
      const durationInDays = daysBetween(startDate, endDate);

      return {
        ...stage,
        startDate,
        endDate,
        durationInDays,
      };
    })
    .filter((stage) => stage.durationInDays > 0);

  return (
    <Card>
      <CardContent>
        <Stack spacing={1.5}>
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="h6">Timeline</Typography>
              <Typography variant="caption" color="text.secondary">
                Relative calendar view from today to the final stage end.
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" textAlign="right">
              {formatDate(chartStartDate)} to {formatDate(chartEndDate)}
            </Typography>
          </Stack>
          <Stack direction="row" width="100%" minHeight={48} borderRadius={1} overflow="hidden">
            {chartSegments.map((stage) => (
              <Box
                key={stage.name}
                sx={{
                  backgroundColor: stage.color,
                  color: stage.name === virtualStage.name ? "text.primary" : "common.white",
                  flexGrow: stage.durationInDays,
                  flexBasis: `${(stage.durationInDays / totalDays) * 100}%`,
                  minWidth: 18,
                  p: 1,
                }}
                title={`${stage.name}: ${formatDate(stage.startDate)} to ${formatDate(stage.endDate)}`}
              >
                <Typography variant="caption" fontWeight={600} noWrap>
                  {stage.name}
                </Typography>
              </Box>
            ))}
          </Stack>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {chartSegments.map((stage) => (
              <Stack key={stage.name} direction="row" spacing={0.75} alignItems="center">
                <Box width={10} height={10} borderRadius="50%" sx={{ backgroundColor: stage.color }} />
                <Typography variant="caption" color="text.secondary">
                  {stage.name}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export const TimelineTool = () => {
  const [targetDate, setTargetDate] = useState(/** @type {import("luxon").DateTime|null} */ (null));
  const timeline = useMemo(() => calculateTimeline(targetDate), [targetDate]);
  const artCompleteDate = timeline[0]?.startDate;

  return (
    <Box pl={2} pr={2}>
      <Stack spacing={2}>
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Box>
                <Typography variant="h4" component="h1">
                  Timeplanner
                </Typography>
                <Typography color="text.secondary">
                  Work backwards from a target date to find the art complete date for printer-ready e-proof files.
                  Weekend dates roll forward to the next Monday and cascade through the plan.
                </Typography>
              </Box>
              <DatePicker
                label="Target date"
                value={targetDate}
                onChange={(date) => setTargetDate(date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    helperText: "The date the game needs to be ready by.",
                  },
                  field: {
                    clearable: true,
                    onClear: () => setTargetDate(null),
                  },
                }}
              />
              {artCompleteDate && <Typography variant="h6">Art complete by {formatDate(artCompleteDate)}</Typography>}
            </Stack>
          </CardContent>
        </Card>

        <TimelineBar timeline={timeline} />

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Stage</TableCell>
                <TableCell align="right">Duration</TableCell>
                <TableCell>Start date</TableCell>
                <TableCell>End date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {timeline.length > 0 ? (
                timeline.map((stage) => (
                  <TableRow
                    key={stage.name}
                    sx={{
                      backgroundColor: `${stage.color}18`,
                      borderLeft: `6px solid ${stage.color}`,
                      "&:last-child td, &:last-child th": { borderBottom: 0 },
                    }}
                  >
                    <TableCell component="th" scope="row">
                      {stage.name}
                    </TableCell>
                    <TableCell align="right">{stage.durationInDays} days</TableCell>
                    <TableCell>
                      {formatDate(stage.startDate)}
                      {stage.startDate > stage.nominalStartDate && (
                        <Typography display="block" variant="caption" color="text.secondary">
                          Rolled forward from {formatDate(stage.nominalStartDate)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{formatDate(stage.endDate)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    Choose a target date to build the timeline.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    </Box>
  );
};
